import { useState, useEffect, useRef, useCallback } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "../ui/input";
import { MapPin } from "lucide-react";

/** A place the person picked out of the dropdown. */
export interface SelectedPlace {
  address: string;
  /**
   * Null when Google returned the place but no point for it.
   *
   * Deliberately null rather than zeros: 0,0 is a real location in the
   * Atlantic, so writing it as a stand-in for "unknown" puts the venue five
   * thousand miles out to sea. Distance search would rank it, and the nightly
   * sweep would skip it as already geocoded, so it would never be corrected.
   */
  coords: { latitude: number; longitude: number } | null;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onPlaceSelect?: (place: SelectedPlace) => void;
  placeholder?: string;
}

type Prediction = google.maps.places.PlacePrediction;

/**
 * Address field with Google's suggestions.
 *
 * Uses the current Places API — `AutocompleteSuggestion`, rather than the
 * `AutocompleteService` this was built on, which Google closed to new
 * customers in March 2025 and is winding down. Two things fall out of the new
 * one beyond staying supported: the chosen place carries its own coordinates,
 * so there is no second round trip to a geocoder to convert the text back into
 * a point, and it can no longer disagree with itself about where the address
 * it just suggested actually is.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Start typing an address...",
}: AddressAutocompleteProps) {
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const latestRequest = useRef(0);

  /**
   * Groups the keystrokes of one search with the selection that ends it.
   *
   * Google bills a session, not each keystroke, so the token is held until a
   * place is chosen — `fetchFields` spends it — and a fresh one starts the
   * next search.
   */
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  onPlaceSelectRef.current = onPlaceSelect;

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!places || !input.trim()) {
        setPredictions([]);
        setOpen(false);
        return;
      }

      const requestId = ++latestRequest.current;
      sessionToken.current ??= new places.AutocompleteSessionToken();

      try {
        const { suggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionToken.current,
            includedRegionCodes: ["us"],
          });

        // A slower reply for an earlier, shorter input must not replace the
        // list the person is actually looking at.
        if (latestRequest.current !== requestId) return;

        const next = suggestions
          .map((s) => s.placePrediction)
          .filter((p): p is Prediction => p !== null);
        setPredictions(next);
        setOpen(next.length > 0);
      } catch {
        if (latestRequest.current !== requestId) return;
        setPredictions([]);
        setOpen(false);
      }
    },
    [places]
  );

  const handleInputChange = (text: string) => {
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void fetchSuggestions(text), 200);
  };

  const handleSelect = async (prediction: Prediction) => {
    setOpen(false);
    setPredictions([]);
    latestRequest.current++; // any suggestion request still in flight is moot

    const fallbackAddress = prediction.text?.text ?? value;
    onChange(fallbackAddress);

    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["formattedAddress", "location"] });
      const location = place.location;
      onPlaceSelectRef.current?.({
        address: place.formattedAddress ?? fallbackAddress,
        coords: location
          ? { latitude: location.lat(), longitude: location.lng() }
          : null,
      });
    } catch {
      // Keep what they picked; the address is still right even when the
      // lookup for its coordinates is not. The nightly sweep will place it.
      onPlaceSelectRef.current?.({ address: fallbackAddress, coords: null });
    } finally {
      // Spent, whether or not the fields came back.
      sessionToken.current = null;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        placeholder={places ? placeholder : "Enter venue address"}
        className="pl-9"
        autoComplete="off"
      />
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      {open && predictions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          {predictions.map((p) => (
            <li
              key={p.placeId}
              onMouseDown={() => void handleSelect(p)}
              className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
            >
              <MapPin className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              {p.text?.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
